import React, { FormEvent } from 'react'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { Icons } from '../assets/icons'
import { useCallback, useEffect, useState } from 'react'
import { Copyright } from '../components/Copyright'
import { FitmeIconSVG } from '../assets/FitmeSVG'
import { BaseSelect } from '../components/BaseSelect'
import { useCommonCtx } from '../context/useCommonCtx'
import { CssBaseline, Link } from '@mui/material'
import { apiService } from '../services/apiServices'
import {
    indexFromLang,
    langIndex2AppLang,
    appLangFromStr,
    langIndexFromLocal,
    AllLanguagesOptions,
    useTranslation,
    uniqStrings,
} from '../i18n/useTranslation'
import { info, logException } from '../services/sentryLogger'
import { isApiDisabled } from '../services/envService'
import { QueryParams } from '../services/types'
import { postLogin } from '../services/helpers'
import { PartialParameters } from '../parameters/types'
import { convertServerParams } from '../parameters/converter'
import { defaultParamsFitme } from '../parameters/defaultParamsFitme'
import { ALL_LANGUAGES, ALL_LANGUAGES_TYPE, LOCAL_MAP } from '../i18n/constants'
import { updateWholeTheme } from '../utils/theme'

export default function SignIn(p: Partial<QueryParams>) {
    const queryParams = {...p} as QueryParams
    const {email, slug, source, language } = queryParams
    const commons = useCommonCtx() // FIXME
    const {params} = commons.state;
    const langIndex = params.assessmentParams['langIndex'];
    const setLangIndex = useCallback(async (langIndex: number) => {
        commons.setState((s) => ({
            ...s,
            params: {
                ...s.params,
                assessmentParams: {
                    ...s.params.assessmentParams,
                    langIndex,
                }
            },
        }))
    }, [])

    const t = useTranslation(commons)

    const [formEmail, setFormEmail] = useState<string>('')
    const [formPassword, setFormPassword] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [serverParams, setServerParams] = useState<PartialParameters>({
        ...defaultParamsFitme,
    })

    useEffect(() => {
        if (slug) {
            apiService.fetchParameters(slug, source).then((params) => {
                // TODO error with fetchParameters Error: Minified MUI error
                //  (index.tsx:103:22) >  at SignIn.tsx:69:17 
                const convertedParams = convertServerParams(params) as unknown as PartialParameters;
                // TODO remove debugs
                console.debug('convertedParams:', convertedParams?.brandParams)
                setServerParams(convertedParams);
                updateWholeTheme(convertedParams?.brandParams ?? defaultParamsFitme.brandParams);
            }).catch((error) => {
                logException('fetchParameters', error)
                if(error?.response?.status === 400 || error?.statusCode == 400) {
                    // invalid slug ...
                    setError(t('no_slug_error'))
                } else {
                    setError(t('unknown_error'))
                }
            })
        }
    }, [slug, source])

    useEffect(() => {
        if (language?.length) {
            const langIndex = langIndexFromLocal(language);
            setLangIndex(langIndex)
        }
    }, [language])

    useEffect(() => {
        if (email?.length) {
            setFormEmail(email)
        }
    }, [email])

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        info('handleSubmit', isApiDisabled(), {
            ...queryParams,
            formEmail,
            formPassword,
        })

        if (formEmail && formPassword) {
            setError(null)
            setLoading(true)
            try {
                const token = await apiService.login(formEmail, formPassword)
                if (!token) throw new Error('No token')
                queryParams.token = token
                await postLogin(queryParams as Required<QueryParams>, commons, serverParams)
            } catch (error: any) {
                if (error?.response?.status === 401) {
                    logException('login', 'Unauthorized', error)
                    setError(t('invalid_credentials')) // FIXME
                } else if (error?.message?.includes('No slug')) {
                    logException('login', 'No slug', error)
                    setError(t('no_slug_error')) // FIXME
                } else {
                    logException('login', 'UNK', error)
                    setError(t('unknown_error')) // FIXME
                    logException('login', 'Unknown', error)
                }
            } finally {
                setLoading(false)
            }
        } else {
            info('No formEmail or formPassword provided, ignored')
        }
    }

    // forgotformPassword
    // Only the en-en is supported for now
    // FIXME use ${localFromAppLang(lang)}
    const forgotLink = `https://assessment.twegos.com/en-en/auth/forgotpassword/eyJpdiI6ImsxRmF0UXk5b1RYR0J0QXVcL3VRVGxBPT0iLCJ2YWx1ZSI6IjcwVjhlOUhQeVIwa21wR3RmdzQ3U1dRSkRSb2V0QXUrbjE2cUYxakR0WFZUVzZXaGwwQVFEWlV6b0Iya3hraVNwNDEwVTd0azBaRnd4clF4cWZ5QldWc2FVZisrc3lVTXA1b2liZzFLWkhFUlJcL0dJdGZiSWhxa2lrdDU1VUw3WmRsbkx6c1NYZUJxVFR2M01oZHdRYVE9PSIsIm1hYyI6IjQ1OWJjOTZmYTk0NGUwMTFiOTAzMmMzODMzNjhkYWEwY2E5MWI4ZDQ0NWQ0Yzk4MmVkMDE5MDkyMDQxMzA1MzIifQ==`
    // const signupLink = `https://assessment.twegos.com/${localFromAppLang(lang)}/auth/signup/step1`;

    const { brandParams } = serverParams
    const availableLanguages  = brandParams?.availableLanguages?.length
        ? uniqStrings(brandParams.availableLanguages).map(appLangFromStr)
        : defaultParamsFitme.brandParams?.availableLanguages
    const sortedAvailableLanguagesOptions = AllLanguagesOptions.filter((l) =>
        availableLanguages.includes(ALL_LANGUAGES[l.v])
    ).sort((a, b) =>
        LOCAL_MAP[langIndex2AppLang(a.v)].label.localeCompare(
            LOCAL_MAP[langIndex2AppLang(b.v)].label
        )
    )

    useEffect(() => {
        if (availableLanguages?.length) {
            const initialLang = language?.length
                ? appLangFromStr(language)
                : appLangFromStr(availableLanguages[0])
            if (
                language?.length &&
                !availableLanguages.includes(appLangFromStr(language))
            ) {
                logException('Provided language not available', {
                    language,
                    availableLanguages,
                })
            } else if (!language) {
                console.warn('Language not set, using default', initialLang)
            }
            setLangIndex(indexFromLang(initialLang))
        }
    }, [availableLanguages?.length])

    // FIXME obsolete
    const isRedictedFromSignup = email?.length && source !== '360'

    return (
        <Grid
            container
            className="flex flex-1 mt-20 items-center justify-center"
            component="main"
            sx={{ width: '100%' }}
        >
            <CssBaseline />

            <Grid
                size={{ xs: 10, sm: 8, md: 5 }}
                component={Paper}
                elevation={6}
                square
            >
                <Box
                    sx={{
                        my: 3,
                        mx: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    {/* FIXME placement */}
                    <Box className="text-5xl sm:text-7xl top-5 left-5 absolute">
                        <FitmeIconSVG
                            inheritViewBox
                            fontSize="inherit"
                            color="disabled"
                        />
                    </Box>

                    {!brandParams?.companyLogoUrl && (
                        <Avatar sx={{ m: 1, bgcolor: 'black' }}>
                            <Icons.LockOutlinedIcon />
                        </Avatar>
                    )}

                    {brandParams?.companyLogoUrl && (
                        <img
                            src={brandParams.companyLogoUrl}
                            className="object-contain m-2"
                            alt="logo"
                            height={75}
                            width={75}
                        />
                    )}

                    <Typography
                        component="h1"
                        variant="h5"
                        className="text-center"
                    >
                        {(isRedictedFromSignup
                            ? t('signin_redirect_title')
                            : t('signin_title')) +
                            (brandParams?.companyName
                                ? ' - ' + brandParams?.companyName
                                : '')}
                    </Typography>
                    <Box
                        component="form"
                        noValidate
                        onSubmit={handleSubmit}
                        sx={{ mt: 1 }}
                    >
                        <TextField
                            error={!!error}
                            helperText={error}
                            size="small"
                            disabled={loading}
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            name="email"
                            autoComplete="email"
                            label={t('email_placeholder')}
                            autoFocus
                            value={formEmail}
                            onChange={({ target: { value } }) =>
                                setFormEmail(value)
                            }
                        />
                        <TextField
                            error={!!error}
                            // helperText={error}
                            size="small"
                            disabled={loading}
                            margin="normal"
                            required
                            fullWidth
                            name="formPassword"
                            label={t(
                                isRedictedFromSignup
                                    ? 'psw_placeholder_redirect'
                                    : 'psw_placeholder'
                            )}
                            type="password"
                            id="formPassword"
                            autoComplete="current-password"
                            value={formPassword}
                            onChange={({ target: { value } }) => {
                                console.debug('pasw field', value)
                                setFormPassword(value)
                            }}
                        />
                        
                        {!language?.length && (
                            <BaseSelect
                                className="w-full mt-5"
                                label={t('language')}
                                value={langIndex}
                                onChange={({ target: { value } }) =>
                                    setLangIndex(value as number)
                                }
                                options={sortedAvailableLanguagesOptions}
                            />
                        )}
                        <Button
                            disabled={loading}
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                        >
                            {loading && t('loading')}
                            {!loading &&
                                (isRedictedFromSignup
                                    ? t('btn_confirm')
                                    : t('btn_signin'))}
                        </Button>
                        <Grid container className="text-center">
                            <Grid size="grow">
                                <Link href={forgotLink} variant="caption">
                                    {t('forgot_password')}
                                </Link>
                            </Grid>
                            {/* <Grid item>
                                <Link href={signupLink} variant="caption">
                                    {t('signup_hint')}
                                </Link>
                            </Grid>  */}
                        </Grid>
                        <Copyright />
                    </Box>
                </Box>
            </Grid>
        </Grid>
    )
}
